import { HStack, Heading, Image, Separator } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import OptionsMenu, {
  OptionsMenuItem,
  OptionsMenuProps
} from "@pathwright/ux/src/components/menu/OptionsMenu"
import {
  HomeRegistrationFragment,
  RegistrationRoleEnum
} from "@pathwright/graphql-client/src/generated"
import { hasEditorLevelAccess } from "../../../group/permissions"
import { usePathCreatorContext } from "../../../path/create/PathCreatorContext"
import { registrationRoleIntMap } from "../../../path/create/RoleBadge"
import { usePathwrightContext } from "../../../pathwright/PathwrightContext"
import { usePositioningContext } from "../../../positioning/PositioningContext"
import { useResourceCopy } from "../../../resource/manage/ResourceCopyControl"
import { canEditLibrary } from "../../../user/permissions"
import { useHomeContext } from "../../HomeContext"
import useSoftArchiveRegistration from "../../registration/useSoftArchiveRegistration"
import { GroupAvatars } from "../GroupAvatars"
import GroupNotificationsFormOption from "../GroupNotificationsFormOption"

type PathCardRegistrationControlsProps = {
  registration: HomeRegistrationFragment
  parentRegistration?: HomeRegistrationFragment
}

const PathCardRegistrationControls = ({
  registration,
  parentRegistration,
  triggerProps,
  ...optionMenuProps
}: PathCardRegistrationControlsProps & OptionsMenuProps) => {
  const pwContext = usePathwrightContext()
  const { t } = useTranslate()
  const positioningContext = usePositioningContext()
  const { setInitialValue } = usePathCreatorContext()
  const getPendingReviewCount = useHomeContext()?.getPendingReviewCount
  const { resource, group } = registration
  const resourceCopy = useResourceCopy(resource)

  function handleMoveToTop() {
    positioningContext.updatePositionedObject({
      item: registration,
      // Note: currently a bug in positional-indexing results in every
      // item being set to "!" (bug) which results in an ISO (as should be expected).

      // Actually, only happens when a positioned registration exists in the target position
      // but is not present in the list (i.e. if the regisration holding the target position
      // is archived).
      index: 0
    })
  }

  const [softArchive] = useSoftArchiveRegistration(registration)
  // You can only soft unarchive a child registration when it's parent
  // is not soft archived.
  const canSoftUnarchive =
    !parentRegistration || !parentRegistration.softArchivedTime

  function handleCreateNewCohort() {
    const resource = registration.resource!
    const group = registration.group!
    const role = registrationRoleIntMap.get(registration.role!)
      ?.enum! as RegistrationRoleEnum.Editor

    setInitialValue({
      resource: {
        id: resource.id!,
        image: resource.cover_image!,
        background_image: resource.background_image!,
        name: resource.name!,
        role,
        sourceCohort: {
          id: group.id!,
          role
        }
      },
      cohort: null
    })
  }

  return (
    <OptionsMenu
      className="PathCardRegistrationControls"
      triggerProps={{
        "aria-label": "Path Card Controls",
        variant: "solid",
        colorPalette: "whiteAlpha",
        ...triggerProps
      }}
      // Render nested registration's controls in a portal to avoid overflow clipping.
      renderPortal={!!registration.group?.parent_section_id}
      renderOnGroupHover
      {...optionMenuProps}
    >
      {!registration.group?.parent_section_id && !!positioningContext && (
        <OptionsMenuItem
          icon="arrow-up-circle"
          label={t("Move to top")}
          onClick={handleMoveToTop}
          closeOnClick
        />
      )}
      <GroupNotificationsFormOption registration={registration} />

      {hasEditorLevelAccess(pwContext, group) && resourceCopy.enabled && (
        <OptionsMenuItem
          icon="copy"
          label={resourceCopy.copied ? t("Copied!") : t("Copy")}
          onClick={resourceCopy.toggleCopy}
          closeOnClick
        />
      )}

      {registration.softArchivedTime ? (
        canSoftUnarchive ? (
          <OptionsMenuItem
            icon="sidebar-bottom"
            iconProps={{
              transform: "rotate(90deg)"
            }}
            label={t("Add to Home Screen")}
            onClick={() => softArchive()}
          />
        ) : null
      ) : (
        <OptionsMenuItem
          icon="sidebar-bottom"
          iconProps={{
            transform: "rotate(-90deg)"
          }}
          label={t("Set aside")}
          onClick={() => softArchive()}
        />
      )}

      {/* {canEditLibrary(pwContext) && (
            //! Check action
            <OptionsMenuItem
              icon="x-circle"
              label="Leave cohort"
              onClick={() => {
                console.log("Edit")
              }}
            />
          )} */}

      {hasEditorLevelAccess(pwContext, group) &&
        !!registration.group?.is_source_cohort && (
          <OptionsMenuItem
            icon="group-plus"
            label={t("New cohort")}
            onClick={handleCreateNewCohort}
            closeOnClick
          />
        )}

      {/* <RoleMenu registration={registration} /> */}

      {canEditLibrary(pwContext) && (
        <>
          <Separator w="full" borderBottomWidth={0} borderTopWidth="1px" />
          <HStack alignItems="center" py={1.5} px={2} maxW="300px">
            <Image
              src={resource?.cover_image}
              maxW="2em"
              borderRadius="md"
              // fallback={
              //   <Box w="2em" h="1.5em" bg="gray.200" borderRadius="md" />
              // }
              _hover={{
                opacity: ".8"
              }}
            />
            <Heading as="h3" fontSize="sm" lineClamp={1} m={0}>
              {resource?.name}
            </Heading>
          </HStack>

          <OptionsMenuItem
            icon="gear"
            label={t("Path Settings")}
            to={`/manage/resource/${resource?.id}/`}
            closeOnClick
          />

          <OptionsMenuItem
            icon="inbox"
            label={t("Review")}
            to={`/inbox/?cohort_id=${group?.id}`}
            indicator={!!getPendingReviewCount?.(registration)}
          />

          {/* <RoleMenu registration={registration} /> */}
          <OptionsMenuItem
            icon="trash"
            label={t("Delete Path")}
            to={`/manage/resource/${resource?.id}/archive/`}
            closeOnClick
          />
        </>
      )}

      {hasEditorLevelAccess(pwContext, group) && !group?.is_source_cohort && (
        <>
          <Separator />
          <HStack alignItems="center" py={1.5} px={2} maxW="300px">
            <GroupAvatars group={group} showGroupName={false} />
            <Heading as="h3" fontSize="sm" lineClamp={1} m={0}>
              {group?.name}
            </Heading>
          </HStack>

          <OptionsMenuItem
            icon="gear"
            label={t("Cohort Settings")}
            to={`/manage/group/${resource?.id}/${group?.id}/`}
            closeOnClick
          />

          {
            // Prevent accessing the archive card for child Cohorts as we don't allow directly
            // archiving child Cohorts, only via archiving the parent (Collection) Cohort.
            !group?.parent_section_id && (
              <OptionsMenuItem
                icon="trash"
                label={t("Delete Cohort")}
                to={`/manage/group/${resource?.id}/${group?.id}/archive/`}
                closeOnClick
              />
            )
          }
        </>
      )}
    </OptionsMenu>
  )
}

export default PathCardRegistrationControls
